import type { PostComment, PublicPost } from '@/src/features/posts/post-service';
import { persistentStorage } from '@/src/lib/persistent-storage';

const cachePrefix = 'frienzzi-feed-cache-v1';
type CacheableDate =
  | Date
  | { toMillis: () => number }
  | number
  | string
  | null
  | undefined;

function cacheKey(key: string) {
  return `${cachePrefix}:${key}`;
}

export async function loadCachedPosts(key: string): Promise<PublicPost[]> {
  const rawValue = await persistentStorage.getItem(cacheKey(key));

  if (!rawValue) {
    return [];
  }

  return (JSON.parse(rawValue) as PublicPost[]).map((post) => ({
    ...post,
    feedStats: post.feedStats ?? {
      commentOpenCount: 0,
      detailOpenCount: 0,
      dwellBucketTotal: 0,
      impressionCount: 0,
    },
    createdAt: dateFromCacheValue(post.createdAt as CacheableDate),
    updatedAt: dateFromCacheValue(post.updatedAt as CacheableDate),
  }));
}

export async function saveCachedPosts(key: string, posts: PublicPost[]) {
  await persistentStorage.setItem(
    cacheKey(key),
    JSON.stringify(
      posts.map((post) => ({
        ...post,
        createdAt: dateToCacheValue(post.createdAt as CacheableDate),
        updatedAt: dateToCacheValue(post.updatedAt as CacheableDate),
      })),
    ),
  );
}

export async function loadCachedComments(
  postId: string,
): Promise<PostComment[]> {
  const rawValue = await persistentStorage.getItem(cacheKey(`comments:${postId}`));

  if (!rawValue) {
    return [];
  }

  return (JSON.parse(rawValue) as PostComment[]).map((comment) => ({
    ...comment,
    createdAt: dateFromCacheValue(comment.createdAt as CacheableDate),
    updatedAt: dateFromCacheValue(comment.updatedAt as CacheableDate),
  }));
}

export async function saveCachedComments(
  postId: string,
  comments: PostComment[],
) {
  await persistentStorage.setItem(
    cacheKey(`comments:${postId}`),
    JSON.stringify(
      comments.map((comment) => ({
        ...comment,
        createdAt: dateToCacheValue(comment.createdAt as CacheableDate),
        updatedAt: dateToCacheValue(comment.updatedAt as CacheableDate),
      })),
    ),
  );
}

function dateToCacheValue(value: CacheableDate) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  return value.toMillis();
}

function dateFromCacheValue(value: CacheableDate) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return new Date(value);
  }

  return new Date(value.toMillis());
}
